import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { FileText, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
export const Login = () => {
  const {
    login,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // If user is already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" data-id="element-249" />;
  }
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // Reset error
    setError('');
    // Basic validation
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Demo credentials check - in a real app, this would be an API call
      if (email === 'demo@example.com' && password === 'password') {
        login({
          id: '1',
          name: 'John Doe',
          email: 'demo@example.com',
          role: 'employee',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
        });
        navigate('/');
      } else if (email === 'manager@example.com' && password === 'password') {
        login({
          id: '2',
          name: 'Jane Smith',
          email: 'manager@example.com',
          role: 'manager',
          avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
        });
        navigate('/');
      } else if (email === 'admin@example.com' && password === 'password') {
        login({
          id: '3',
          name: 'Robert Johnson',
          email: 'admin@example.com',
          role: 'admin',
          avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
        });
        navigate('/');
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-bg-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-250">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-251">
        <div className="flex justify-center" data-id="element-252">
          <div className="h-12 w-12 rounded-full bg-brand-100 flex items-center justify-center" data-id="element-253">
            <FileText className="h-7 w-7 text-brand-600" data-id="element-254" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-text-primary" data-id="element-255">
          Sign in to TaskFiles
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary" data-id="element-256">
          Enter your credentials to access your account
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-257">
        <div className="bg-surface-50 py-8 px-4 shadow-medium sm:rounded-lg sm:px-10 border border-border" data-id="element-258">
          {error && <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-md flex items-start" data-id="element-259">
              <AlertCircle className="h-5 w-5 text-error-500 flex-shrink-0 mr-2 mt-0.5" data-id="element-260" />
              <p className="text-sm text-error-700" data-id="element-261">{error}</p>
            </div>}
          <form className="space-y-6" onSubmit={handleLogin} data-id="element-262">
            <div data-id="element-263">
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1" data-id="element-264">
                Email address
              </label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" leftIcon={<Mail className="h-5 w-5 text-text-tertiary" data-id="element-266" />} autoComplete="email" required data-id="element-265" />
            </div>
            <div data-id="element-267">
              <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1" data-id="element-268">
                Password
              </label>
              <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" leftIcon={<Lock className="h-5 w-5 text-text-tertiary" data-id="element-270" />} rightIcon={<button type="button" onClick={() => setShowPassword(!showPassword)} className="text-text-tertiary hover:text-text-secondary" data-id="element-271">
                    {showPassword ? <EyeOff className="h-5 w-5" data-id="element-272" /> : <Eye className="h-5 w-5" data-id="element-273" />}
                  </button>} autoComplete="current-password" required data-id="element-269" />
            </div>
            <div className="flex items-center justify-between" data-id="element-274">
              <div className="flex items-center" data-id="element-275">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded" data-id="element-276" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-text-secondary" data-id="element-277">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-278">
                <a href="#" className="font-medium text-brand-600 hover:text-brand-500" data-id="element-279">
                  Forgot your password?
                </a>
              </div>
            </div>
            <Button type="submit" fullWidth isLoading={isLoading} data-id="element-280">
              Sign in
            </Button>
          </form>
          <div className="mt-6" data-id="element-281">
            <div className="relative" data-id="element-282">
              <div className="absolute inset-0 flex items-center" data-id="element-283">
                <div className="w-full border-t border-border" data-id="element-284"></div>
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-285">
                <span className="px-2 bg-surface-50 text-text-tertiary" data-id="element-286">
                  Demo accounts
                </span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3" data-id="element-287">
              <div className="rounded-md border border-border p-3" data-id="element-288">
                <p className="text-sm font-medium text-text-primary" data-id="element-289">
                  Employee
                </p>
                <p className="text-xs text-text-secondary mt-1" data-id="element-290">
                  demo@example.com / password
                </p>
              </div>
              <div className="rounded-md border border-border p-3" data-id="element-291">
                <p className="text-sm font-medium text-text-primary" data-id="element-292">Manager</p>
                <p className="text-xs text-text-secondary mt-1" data-id="element-293">
                  manager@example.com / password
                </p>
              </div>
              <div className="rounded-md border border-border p-3" data-id="element-294">
                <p className="text-sm font-medium text-text-primary" data-id="element-295">Admin</p>
                <p className="text-xs text-text-secondary mt-1" data-id="element-296">
                  admin@example.com / password
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};